import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { supabase } from "../../supabaseClient";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { TournamentContext } from "../../context/TournamentContext";
import { tournamentPath } from "../../utility/tournamentUrls";

const TOURNAMENT_NAME_PLACEHOLDER = "[Tournament Name]";
const GENERIC_TOURNAMENT_LABEL = "Tournament";

const pathFor = (item) =>
  item?.path || (item?.href && item.href !== "#" ? item.href : null);

const isTournamentCrumb = (item, tournamentSlug) => {
  const itemPath = pathFor(item);
  if (!itemPath) return false;
  return (
    itemPath.includes(tournamentPath(tournamentSlug)) &&
    !itemPath.includes("/squads") &&
    !itemPath.includes("/teams") &&
    !itemPath.includes("/results") &&
    !itemPath.includes("/standings") &&
    itemPath === tournamentPath(tournamentSlug)
  );
};

// Unresolved tournament crumbs: a blank label, the old "[Tournament Name]"
// template, or the generic word "Tournament". A real name stays as written
// (Create Tournament, the Tournaments list, and an explicit tournament name).
const labelNeedsTournamentName = (label) => {
  const text = typeof label === "string" ? label.trim() : "";
  return (
    text === "" ||
    text === TOURNAMENT_NAME_PLACEHOLDER ||
    text === GENERIC_TOURNAMENT_LABEL
  );
};

const Breadcrumb = ({ items }) => {
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const tournamentContext = useContext(TournamentContext);
  const contextName =
    tournamentContext?.tournamentId &&
    String(tournamentContext.tournamentId) === String(tournamentId) &&
    tournamentContext.tournamentName
      ? tournamentContext.tournamentName
      : "";
  const [tournamentName, setTournamentName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  // Prefer the provider name so a failed breadcrumb fetch cannot stick the
  // generic fallback over a name that loads a moment later.
  const resolvedName = contextName || tournamentName;

  useEffect(() => {
    let cancelled = false;

    const fetchTournamentName = async () => {
      if (!tournamentId) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      const needsFetch = items.some(
        (item) =>
          isTournamentCrumb(item, tournamentSlug) &&
          labelNeedsTournamentName(item.label)
      );

      // Client navigations already have the name from TournamentProvider.
      if (!needsFetch || contextName) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("tournaments")
          .select("name")
          .eq("id", tournamentId)
          .single();

        if (cancelled) return;

        if (error || !data?.name) {
          if (error) console.error("Error fetching tournament name:", error);
          setTournamentName("");
        } else {
          setTournamentName(data.name);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error in breadcrumb:", error);
        setTournamentName("");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTournamentName();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, tournamentSlug, contextName, items]);

  const labelFor = (item) => {
    if (!isTournamentCrumb(item, tournamentSlug)) return item.label;
    if (!labelNeedsTournamentName(item.label)) return item.label;
    if (resolvedName) return resolvedName;
    return isLoading ? "Loading..." : "Tournament";
  };

  // Function to render a single breadcrumb item
  const renderBreadcrumbItem = (item, index, isLast = false) => {
    const itemPath = pathFor(item);
    const label = labelFor(item);

    return (
      <li key={index} className="inline-flex items-center min-w-0">
        {index > 0 && (
          <svg
            className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1.5 md:mx-2 flex-shrink-0 transition-transform duration-150 hover:scale-105"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 6 10"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="m1 9 4-4-4-4"
            />
          </svg>
        )}
        {itemPath && !isLast ? (
          <Link
            to={itemPath}
            className="inline-flex items-center min-h-11 text-sm font-medium text-brand-muted hover:text-brand-gold-ink transition-colors duration-150 ease-in-out truncate max-w-[150px] lg:max-w-none"
            aria-current={isLast ? "page" : undefined}
            title={label}
          >
            {label}
          </Link>
        ) : (
          <span
            className="text-sm font-medium text-brand-charcoal truncate max-w-[100px] sm:max-w-[150px] md:max-w-none"
            aria-current="page"
            title={label}
          >
            {label}
          </span>
        )}
      </li>
    );
  };

  const current = items[items.length - 1];
  const parent = items.length > 1 ? items[items.length - 2] : null;
  const parentPath = pathFor(parent);
  const currentLabel = current ? labelFor(current) : "";
  const parentLabel = parent ? labelFor(parent) : "";

  return (
    <nav
      className="flex items-center px-3 max-w-full overflow-hidden min-w-0 w-full"
      aria-label="Breadcrumb"
    >
      {/* Phone: back to the parent, plus the current page. */}
      <div className="flex md:hidden items-center gap-2 min-w-0 flex-1 min-h-11">
        {parentPath ? (
          <Link
            to={parentPath}
            className="inline-flex items-center gap-1 min-h-11 shrink-0 max-w-[55%] px-1 text-sm font-medium text-brand-gold-ink"
          >
            <svg
              className="w-4 h-4 shrink-0"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 6 10"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M5 1 1 5l4 4"
              />
            </svg>
            <span className="truncate">{parentLabel || "Back"}</span>
          </Link>
        ) : null}
        <span
          className="truncate text-sm font-medium text-brand-charcoal min-w-0"
          aria-current="page"
          title={currentLabel}
        >
          {currentLabel}
        </span>
      </div>

      {/* Tablet and desktop: full trail */}
      <ol className="hidden md:inline-flex items-center space-x-2 rtl:space-x-reverse min-w-0 flex-1">
        {items.map((item, index) =>
          renderBreadcrumbItem(item, index, index === items.length - 1)
        )}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
